<template>
  <!-- HERO -->
  <section id="hero" class="hero">

    <div class="container hero-container">
      <div class="row align-items-center">

        <div class="col-lg-7 hero-content">
          <div class="hero-badge animate-fade-up" style="animation-delay: 0.1s">
            <span class="badge-dot"></span>
            {{ $t('hero.badge') }}
          </div>
          <h1 class="hero-headline">
            <span class="hero-line animate-line" style="animation-delay: 0.2s">{{ $t('hero.line1') }}</span>
            <span class="hero-line animate-line" style="animation-delay: 0.38s">{{ $t('hero.line2') }}</span>
            <span class="hero-line hero-line--accent animate-line" style="animation-delay: 0.56s">{{ $t('hero.line3') }}</span>
          </h1>
          <p class="hero-subtitle animate-fade-up" style="animation-delay: 0.75s">{{ $t('hero.subtitle') }}</p>
          <div class="hero-ctas animate-fade-up" style="animation-delay: 0.9s">
            <router-link class="btn-hero-primary" to="/#contact">{{ $t('hero.cta_primary') }}</router-link>
            <router-link class="btn-hero-outline" to="/portfolio">{{ $t('hero.cta_secondary') }}</router-link>
          </div>
        </div>

        <div class="col-lg-5 d-none d-lg-block code-col">
          <div class="code-card animate-slide-left" style="animation-delay: 0.45s" v-tilt="6">
            <div class="code-glare"></div>
            <div class="code-card-header">
              <span class="dot dot-red"></span>
              <span class="dot dot-yellow"></span>
              <span class="dot dot-green"></span>
              <span class="code-card-title">pixelated.ts</span>
            </div>
            <div class="code-card-body">
              <p class="code-line"><span class="comment">{{ $t('hero.code_comment') }}</span></p>
              <p class="code-line">&nbsp;</p>
              <p class="code-line"><span class="kw">const</span> <span class="var">pixelated</span> <span class="op">=</span> {</p>
              <p class="code-line"><span class="indent"><span class="key">bouwt</span><span class="op">:</span>    [<span class="str">'web'</span>, <span class="str">'apps'</span>, <span class="str">'maatwerk'</span>],</span></p>
              <p class="code-line"><span class="indent"><span class="key">aanpak</span><span class="op">:</span>   [<span class="str">'design'</span>, <span class="str">'build'</span>, <span class="str">'launch'</span>],</span></p>
              <p class="code-line"><span class="indent"><span class="key">snelheid</span><span class="op">:</span> <span class="str">'live in weken'</span>,</span></p>
              <p class="code-line"><span class="indent"><span class="key">contact</span><span class="op">:</span>  <span class="str">'direct, geen tussenlagen'</span>,</span></p>
              <p class="code-line">}</p>
              <p class="code-line">&nbsp;</p>
              <p class="code-line"><span class="kw">export default</span> <span class="var">pixelated</span><span class="op"> =></span> <span class="str">'{{ $t("hero.code_output") }}'</span></p>
              <p class="code-line"><span class="cursor-line"><span class="typing-cursor">▌</span></span></p>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div class="hero-scroll-hint animate-fade-up" style="animation-delay: 1.2s">
      <span>{{ $t('hero.scroll') }}</span>
      <div class="scroll-line"></div>
    </div>
  </section>

  <div id="diensten"><ServicesPage /></div>
  <div id="portfolio"><PlayersComponent /></div>
  <div id="over-mij"><FAQComponent /></div>
  <div id="hosting"><HostingSection /></div>
  <ContactPage />
</template>

<script setup>
import ServicesPage from "@/components/ServicesComponent.vue"
import PlayersComponent from "@/components/PlayersComponent.vue"
import FAQComponent from "@/components/FAQComponent.vue"
import HostingSection from "@/components/HostingComponent.vue"
import ContactPage from "@/components/ContactComponent.vue"
</script>

<style scoped>
/* ─── Entrance animations ─────────────────────── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes slideLine {
  from { opacity: 0; transform: translateX(-18px); }
  to   { opacity: 1; transform: translateX(0); }
}

@keyframes slideLeft {
  from { opacity: 0; transform: translateX(32px); }
  to   { opacity: 1; transform: translateX(0); }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
}

@keyframes scrollPulse {
  0%   { transform: scaleY(1); opacity: 0.6; }
  50%  { transform: scaleY(1.6); opacity: 1; }
  100% { transform: scaleY(1); opacity: 0.6; }
}

.animate-fade-up {
  opacity: 0;
  animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.animate-line {
  opacity: 0;
  animation: slideLine 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.animate-slide-left {
  opacity: 0;
  animation: slideLeft 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.typing-cursor {
  animation: blink 1s step-end infinite;
  color: var(--accent);
}

/* ─── Hero ──────────────────────────────────── */
.hero {
  min-height: 100svh;
  background-color: transparent;
  display: flex;
  align-items: center;
  position: relative;
  padding-top: 5rem;
  padding-bottom: 3rem;
}

.hero-container {
  position: relative;
  z-index: 2;
}

.hero-content {
  padding: 3rem 0;
}

/* ─── Badge ──────────────────────────────────── */
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-soft);
  background: var(--accent-tint);
  border: 1px solid var(--accent-line);
  border-radius: 100px;
  padding: 0.35rem 0.875rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(4px);
}

.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.7);
  animation: blink 2.5s ease-in-out infinite;
}

/* ─── Headline ───────────────────────────────── */
.hero-headline {
  display: flex;
  flex-direction: column;
  font-family: 'Jersey 15', sans-serif;
  font-size: clamp(2.25rem, 7vw, 4.5rem);
  font-weight: 400;
  color: #fff;
  line-height: 1.1;
  letter-spacing: 0;
  margin-bottom: 1.75rem;
}

.hero-line {
  display: block;
}

.hero-line--accent {
  background: linear-gradient(92deg, var(--accent) 0%, var(--accent-soft) 60%, #fff 130%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 24px var(--accent-glow));
}

.hero-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.75;
  margin-bottom: 2.5rem;
  max-width: 480px;
}

.hero-ctas {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-hero-primary {
  display: inline-block;
  background: var(--accent-grad);
  color: #0a0a0a;
  padding: 0.9rem 2.1rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 6px 30px var(--accent-glow);
  transition: filter 0.2s, transform 0.2s, box-shadow 0.2s;
}

.btn-hero-primary:hover {
  filter: brightness(1.06);
  color: #0a0a0a;
  transform: translateY(-2px);
  box-shadow: 0 10px 40px var(--accent-glow);
  text-decoration: none;
}

.btn-hero-outline {
  display: inline-block;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  padding: 0.875rem 2rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 0.2s, color 0.2s, transform 0.2s;
}

.btn-hero-outline:hover {
  border-color: rgba(255, 255, 255, 0.5);
  color: #fff;
  transform: translateY(-2px);
  text-decoration: none;
}

/* ─── Code card ──────────────────────────────── */
.code-col { perspective: 1200px; }

.code-card {
  position: relative;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  border: 1px solid var(--border-strong);
  border-radius: 14px;
  overflow: hidden;
  box-shadow:
    0 30px 80px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(250, 168, 43, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transform-style: preserve-3d;
}

/* Specular glare that tracks the cursor via --mx/--my from v-tilt */
.code-glare {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  border-radius: 14px;
  opacity: 0;
  background: radial-gradient(
    340px circle at var(--mx, 50%) var(--my, 50%),
    rgba(250, 168, 43, 0.18),
    transparent 60%
  );
  transition: opacity 0.3s;
}
.code-card:hover .code-glare { opacity: 1; }

.code-card-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.dot { width: 12px; height: 12px; border-radius: 50%; }
.dot-red    { background: #ff5f57; }
.dot-yellow { background: #febc2e; }
.dot-green  { background: #28c840; }

.code-card-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.35);
  margin-left: 0.5rem;
}

.code-card-body { padding: 1.5rem 1.75rem; }

.code-line {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
}

.cursor-line { line-height: 1.4; }
.indent  { padding-left: 1.5rem; }
.indent2 { padding-left: 3.75rem; }
.kw      { color: var(--accent); }
.var     { color: #ffffff; }
.key     { color: var(--accent-soft); }
.op      { color: rgba(255, 255, 255, 0.4); }
.str     { color: #ffd591; }
.comment { color: rgba(255, 255, 255, 0.3); font-style: italic; }

/* ─── Scroll hint ────────────────────────────── */
.hero-scroll-hint {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  z-index: 2;
}

.hero-scroll-hint span {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.2);
  letter-spacing: 2px;
  text-transform: uppercase;
}

.scroll-line {
  width: 1px;
  height: 32px;
  background: linear-gradient(to bottom, rgba(255,255,255,0.2), transparent);
  animation: scrollPulse 2s ease-in-out infinite;
  transform-origin: top;
}

/* ─── Responsive ─────────────────────────────── */
@media (max-width: 991px) {
  .hero {
    padding-top: 5rem;
    padding-bottom: 3.5rem;
    align-items: center;
  }
  .hero-scroll-hint { display: none; }
}

@media (max-width: 767px) {
  .hero {
    min-height: unset;
    align-items: flex-start;
    padding-top: 6rem;
    padding-bottom: 3rem;
    padding-left: 16px;
    padding-right: 16px;
  }
  .hero-content {
    padding: 0;
  }
  .hero-subtitle {
    max-width: 100%;
    font-size: 1rem;
    margin-bottom: 2rem;
  }
  .hero-ctas {
    flex-direction: column;
  }
  .btn-hero-primary, .btn-hero-outline {
    text-align: center;
    width: 100%;
  }
  .hero-scroll-hint { display: none; }
}
</style>
